import { createSlice, createAction, PayloadAction } from '@reduxjs/toolkit'
import { createResource } from 'app/state/resource'
import { ApiError, RequestStatus } from 'features/common'
import { User } from 'features/users'
import { App, AppTypes, AppsListFilter, FeaturedApp } from '../types'

const name = 'apps'
const APPS_FETCH_REQUEST = `${name}/fetch_apps_request`
const BOOKMARKED_APPS_FETCH_REQUEST = `${name}/fetch_bookmarked_apps_request`
const FEATURED_APPS_FETCH_REQUEST = `${name}/fetch_featured_apps_request`
const APPS_FETCH_APP_REQUEST = `${name}/fetch_app_request`
const APPS_SAVE_REQUEST = `${name}/save_app_request`
const APPS_DELETE_REQUEST = `${name}/delete_app_request`
const APPS_APP_SELECTED_REQUEST = `${name}/track_app_selected`

const myAppsListFilterDefault = {
  orderBy: {
    fieldName: 'updated_at',
    ascending: false
  }
}

export interface AppsState {
  myApps: Array<App>
  featuredApps: Array<FeaturedApp>
  filteredApps: Array<App>
  myAppsListFilter: AppsListFilter
  myAppsStatus: RequestStatus
  featuredAppsStatus: RequestStatus
  myAppsError?: ApiError | null
  featuredAppsError?: ApiError | null
  currentApp?: App
  saveAppStatus: RequestStatus
  saveAppError?: ApiError | null
  deleteAppStatus?: RequestStatus
  deleteAppError?: ApiError | null
  bookmarkedApps: Array<App>
  bookmarkedAppsStatus: RequestStatus
  bookmarkedAppsError?: ApiError | null
}

const initialState: AppsState = {
  myApps: [],
  featuredApps: [],
  filteredApps: [],
  myAppsListFilter: myAppsListFilterDefault,
  myAppsStatus: RequestStatus.Idle,
  featuredAppsStatus: RequestStatus.Idle,
  saveAppStatus: RequestStatus.Idle,
  deleteAppStatus: RequestStatus.Idle,
  bookmarkedApps: [],
  bookmarkedAppsStatus: RequestStatus.Idle
}

const asyncActions = {
  appsRequest: createAction(APPS_FETCH_REQUEST),
  featuredAppsRequest: createAction(FEATURED_APPS_FETCH_REQUEST),
  bookmarkedAppsRequest: createAction(BOOKMARKED_APPS_FETCH_REQUEST),
  appRequest: createAction<{ id: string; type: AppTypes }>(APPS_FETCH_APP_REQUEST),
  saveAppRequest: createAction<App>(APPS_SAVE_REQUEST),
  deleteAppRequest: createAction<{ id: string; type: AppTypes }>(APPS_DELETE_REQUEST),
  trackAppSelectedRequest: createAction<{ appId: string; user: User; type?: string }>(APPS_APP_SELECTED_REQUEST)
}

const reducers = {
  addApps: (state: AppsState, action: PayloadAction<Array<App>>) => {
    state.myApps = action.payload
  },
  addFeaturedApps: (state: AppsState, action: PayloadAction<Array<FeaturedApp>>) => {
    state.featuredApps = action.payload
  },
  setFilteredApps: (state: AppsState, action: PayloadAction<Array<App>>) => {
    state.filteredApps = action.payload
  },
  setMyAppsListFilter: (state: AppsState, action: PayloadAction<AppsListFilter>) => {
    state.myAppsListFilter = action.payload
  },
  resetMyAppsListFilter: (state: AppsState) => {
    state.myAppsListFilter = myAppsListFilterDefault
  },
  setCurrentApp: (state: AppsState, action: PayloadAction<App | undefined>) => {
    state.currentApp = action.payload
  },
  setMyAppsStatus: (state: AppsState, action: PayloadAction<RequestStatus>) => {
    state.myAppsStatus = action.payload
  },
  setFeaturedAppsStatus: (state: AppsState, action: PayloadAction<RequestStatus>) => {
    state.featuredAppsStatus = action.payload
  },
  setMyAppsError: (state: AppsState, action: PayloadAction<ApiError>) => {
    state.myAppsError = action.payload
  },
  setFeaturedAppsError: (state: AppsState, action: PayloadAction<ApiError>) => {
    state.featuredAppsError = action.payload
  },
  setSaveAppStatus: (state: AppsState, action: PayloadAction<RequestStatus>) => {
    state.saveAppStatus = action.payload
  },
  setSaveAppError: (state: AppsState, action: PayloadAction<ApiError | null>) => {
    state.saveAppError = action.payload
  },
  setDeleteAppStatus: (state: AppsState, action: PayloadAction<RequestStatus>) => {
    state.deleteAppStatus = action.payload
  },
  setDeleteAppError: (state: AppsState, action: PayloadAction<ApiError | null>) => {
    state.deleteAppError = action.payload
  },
  setBookmarkedApps: (state: AppsState, action: PayloadAction<Array<App>>) => {
    state.bookmarkedApps = action.payload
  },
  setBookmarkedAppsStatus: (state: AppsState, action: PayloadAction<RequestStatus>) => {
    state.bookmarkedAppsStatus = action.payload
  },
  setBookmarkedAppsError: (state: AppsState, action: PayloadAction<ApiError | null>) => {
    state.bookmarkedAppsError = action.payload
  },
  updateAppsFromList: (state: AppsState, action: PayloadAction<Partial<App>>) => {
    state.myApps = state.myApps.map((app) => {
      if (app.id === action.payload.id) {
        return {
          ...app,
          ...action.payload
        }
      } else {
        return app
      }
    })
    if (state.currentApp && state.currentApp.id === action.payload.id) {
      state.currentApp = { ...state.currentApp, ...action.payload }
    }
  }
}

export const myAppsResource = createResource({
  get: (state) => state.apps.myApps,
  status: (state) => state.apps.myAppsStatus,
  error: (state) => state.apps.myAppsError,
  createAction: () => asyncActions.appsRequest()
})

export const appsSlice = createSlice({
  name,
  initialState,
  reducers
})

export const {
  addApps,
  addFeaturedApps,
  setFilteredApps,
  setMyAppsListFilter,
  resetMyAppsListFilter,
  setCurrentApp,
  setMyAppsStatus,
  setFeaturedAppsStatus,
  setMyAppsError,
  setFeaturedAppsError,
  setSaveAppStatus,
  setSaveAppError,
  setDeleteAppStatus,
  setDeleteAppError,
  setBookmarkedApps,
  setBookmarkedAppsStatus,
  setBookmarkedAppsError,
  updateAppsFromList
} = appsSlice.actions

export const {
  appsRequest,
  featuredAppsRequest,
  appRequest,
  saveAppRequest,
  deleteAppRequest,
  trackAppSelectedRequest,
  bookmarkedAppsRequest
} = asyncActions

export default appsSlice.reducer
